import type { DatabaseConnection, Driver, QueryResult } from 'kysely'
import { CompiledQuery } from 'kysely'
import { useSharedWorker } from '@/composables/useSharedworker'

interface RemoteSqlite {
  query: (sql: string, bind?: any[] | undefined) => any
}

const Worker = useSharedWorker()

export class WasmSqliteDriver implements Driver {
  #db?: RemoteSqlite

  constructor() {}

  async init(): Promise<void> {
    this.#db = Worker
  }

  async acquireConnection(): Promise<DatabaseConnection> {
    // Kysely の SQLite dialect 実装とは異なる。
    // 複数の（疑似）コネクションを許容しMutexは取らない。
    // 代わりに、SQL発行時にLockを獲得する。
    const connection = new WasmSqliteConnection(this.#db!)
    return connection!
  }

  async beginTransaction(connection: WasmSqliteConnection): Promise<void> {
    // トランザクション開始時に、排他ロックを獲得することで
    // Isolation level "Serializable" をエミュレートする
    await connection.acquireExclusive()
    await connection.executeQuery(CompiledQuery.raw('begin'))
  }

  async commitTransaction(connection: WasmSqliteConnection): Promise<void> {
    await connection.executeQuery(CompiledQuery.raw('commit'))
    await connection.releaseExclusive()
  }

  async rollbackTransaction(connection: WasmSqliteConnection): Promise<void> {
    await connection.executeQuery(CompiledQuery.raw('rollback'))
    await connection.releaseExclusive()
  }

  async releaseConnection(): Promise<void> {}

  async destroy(): Promise<void> {}
}

/**
 * Web Lock API のロックを、コールバック関数の外に持ち出す邪悪な関数

 * This function uses the Web Lock API to request an exclusive lock on a resource. 
 * It returns a function that can be used to release the lock.

 * @param name - The name of the resource to lock.
 * @param options - An object containing options for the lock request. 
 *                  See `navigator.locks.request` for details.
 * @returns A promise that resolves to a function that can be used to release the lock.
 *          The release function also logs the time the lock was held for (in milliseconds).
 */
async function requestWebLock(name: string, options: LockOptions): Promise<() => void> {
  let acquirer: () => void = () => {}
  let releaser: () => void = () => {}
  const untilAcquire = new Promise<void>((r) => {
    acquirer = () => {
      r()
    }
  })
  const untilRelease = new Promise<void>((r) => {
    releaser = () => {
      r()
    }
  })
  navigator.locks.request(name, options, async () => {
    acquirer()
    await untilRelease
  })
  await untilAcquire
  return releaser
}

class WasmSqliteConnection implements DatabaseConnection {
  readonly #db: RemoteSqlite
  #isInExclusive: boolean
  #release: () => void
  readonly #lockName = 'wasm-sqlite-driver'

  constructor(db: RemoteSqlite) {
    this.#db = db
    this.#isInExclusive = false
    this.#release = () => {}
  }

  async acquireExclusive(): Promise<void> {
    this.#release = await requestWebLock(this.#lockName, { mode: 'exclusive' })
    this.#isInExclusive = true
  }

  async releaseExclusive(): Promise<void> {
    this.#release()
    this.#isInExclusive = false
  }

  async executeQuery<O>(compiledQuery: CompiledQuery): Promise<QueryResult<O>> {
    const { sql, parameters } = compiledQuery
    if (this.#isInExclusive) {
      // If this connection is in exclusive lock, NEVER request shared lock.
      const result = await this.#db.query(sql, parameters as any[])
      return Promise.resolve({
        rows: result as O[]
      })
    } else {
      // If this connection is not in exclusive lock, MUST acquire shared lock.
      let result
      await navigator.locks.request(this.#lockName, { mode: 'shared' }, async () => {
        result = await this.#db.query(sql, parameters as any[])
      })
      return Promise.resolve({
        rows: result as unknown as O[]
      })
    }
  }

  // eslint-disable-next-line require-yield
  async *streamQuery<R>(): AsyncIterableIterator<QueryResult<R>> {
    throw new Error("Sqlite driver doesn't support streaming")
  }
}
